<template>
  <div class="bg-white rounded shadow border border-gray-100 flex flex-col w-full h-full relative">
    <div class="flex flex-row items-center p-2 border-b border-gray-100 flex-shrink-0">
      <div class="flex flex-row items-center flex-grow min-w-0 pl-2 gap-2">
        <HelpBotAvatar :url="settings.AvatarUrl" :name="settings.BotName" :size="48" />
        <div class="min-w-0">
          <p class="font-medium text-primary text-sm truncate" :style="settings.AccentColor ? { color: settings.AccentColor } : null">{{ settings.BotName }}</p>
          <p class="text-xs text-gray-500 leading-none">{{ viewLabel }}</p>
        </div>
      </div>
      <button v-if="view !== 'chat'" type="button" class="flex-shrink-0" @click="view = 'chat'">
        <font-awesome-icon icon="arrow-left" class="mr-2"></font-awesome-icon>
        Back
      </button>
      <div v-if="view !== 'settings'" class="flex flex-row items-center gap-3 flex-shrink-0">
        <button type="button" class="primary !m-0" :disabled="creating" @click="startConversation">
          <font-awesome-icon icon="plus" class="mr-2"></font-awesome-icon>
          New
        </button>
        <IconButton
          v-if="selectedId"
          icon="download"
          class="!m-0"
          aria-label="Download conversation"
          tooltip="Download conversation"
          @click="openDownload"
        />
        <button
          v-if="selectedId && messageCount > 0"
          type="button"
          class="icon cancel !m-0"
          aria-label="Flag chat"
          v-tooltip="'Flag chat'"
          @click="openFlag"
        >
          <font-awesome-icon icon="flag" />
        </button>
      </div>
      <IconButton icon="times" class="cancel" aria-label="Close Help Chat" @click="emit('close')" />
    </div>

    <HelpChatSettings v-if="view === 'settings'" class="flex-1 min-h-0" @close="view = 'chat'" />
    <HelpChatFiles v-else-if="view === 'files'" class="flex-1 min-h-0" @open-conversation="openConversation" />
    <div v-show="view === 'chat'" class="flex-1 flex flex-col md:flex-row min-h-0">
      <aside class="flex flex-col md:w-60 min-w-0 min-h-0 max-h-40 md:max-h-none border-b md:border-b-0 md:border-r border-gray-100 flex-shrink-0 overflow-hidden">
        <p class="p-2 font-bold text-sm flex-shrink-0">Conversations</p>
        <div class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          <div
            v-for="conversation in conversations"
            :key="conversation.id"
            class="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1 px-3 py-3 border-b border-gray-100 last:border-b-0 border-l-2 cursor-pointer transition-colors hover:bg-gray-100"
            :class="conversation.id === selectedId ? 'bg-gray-100 border-l-primary' : 'bg-gray-50 border-l-white'"
            tabindex="0"
            @click="selectConversation(conversation.id)"
            @keydown.enter="selectConversation(conversation.id)"
          >
            <span
              class="col-start-1 row-start-1 min-w-0 text-sm break-words"
              :class="conversation.id === selectedId ? 'font-bold text-primary' : 'font-normal'"
            >{{ conversation.title }}</span>
            <span class="col-start-1 row-start-2 text-xs leading-none text-gray-500">{{ formatUpdated(conversation.updatedAt) }}</span>
            <button
              v-if="conversation.id === selectedId"
              type="button"
              class="cancel col-start-2 row-start-2 self-center justify-self-end !m-0 !p-0 !leading-none text-xs"
              aria-label="Delete conversation"
              :disabled="deletingId === conversation.id"
              @click.stop="deleteConversation(conversation.id)"
            >
              <font-awesome-icon icon="trash"></font-awesome-icon>
            </button>
          </div>
          <p v-if="!conversations.length" class="p-4 text-center text-gray-400 italic text-sm">
            No conversations yet
          </p>
        </div>
        <div class="flex flex-row items-center flex-shrink-0 border-t border-gray-100">
          <IconButton
            icon="gear"
            :aria-label="`${settings.BotName} settings`"
            tooltip="Settings"
            @click="view = 'settings'"
          />
          <IconButton
            icon="folder"
            aria-label="Files"
            tooltip="Files"
            @click="view = 'files'"
          />
          <IconButton
            v-if="showExpand"
            icon="expand"
            aria-label="Open chat in a window"
            tooltip="Open chat in a window"
            @click="emit('expand')"
          />
        </div>
      </aside>
      <HelpChatContent class="flex-1 min-h-0" :conversation-id="selectedId" />
    </div>

    <div
      v-if="downloadOpen"
      ref="downloadDialog"
      class="absolute inset-0 z-10 flex items-center justify-center p-4"
      style="background: rgba(0, 0, 0, 0.3)"
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-download-title"
      tabindex="-1"
      @click.self="downloadOpen = false"
      @keydown.esc="downloadOpen = false"
    >
      <div class="bg-white rounded shadow border border-gray-100 p-4 w-full max-w-sm">
        <p id="help-download-title" class="font-medium text-primary text-sm">Download conversation</p>
        <p class="text-sm text-gray-500 mt-1 mb-4">Save a readable transcript, or a data file of the messages and files.</p>
        <div class="flex flex-row justify-end gap-2">
          <button type="button" class="cancel" @click="downloadOpen = false">Cancel</button>
          <button type="button" class="primary" @click="saveDownload('text')">Text</button>
          <button type="button" class="primary" @click="saveDownload('data')">Data</button>
        </div>
      </div>
    </div>

    <div
      v-if="flagOpen"
      class="absolute inset-0 z-10 flex items-center justify-center p-4"
      style="background: rgba(0, 0, 0, 0.3)"
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-flag-title"
      @click.self="closeFlag"
      @keydown.esc="closeFlag"
    >
      <form class="bg-white rounded shadow border border-gray-100 p-4 w-full max-w-md max-h-full overflow-y-auto" @submit.prevent="submitFlag">
        <p id="help-flag-title" class="font-medium text-primary text-sm">Flag Chat</p>
        <p class="text-sm text-gray-500 mt-1 mb-3">Describe what went wrong. This chat is sent with your notes.</p>
        <label class="block text-sm mb-3" for="help-flag-wrong">
          What is wrong
          <textarea
            id="help-flag-wrong"
            ref="flagWrong"
            v-model="flagNotes.wrong"
            class="w-full border rounded p-2 mt-1 min-h-20 text-sm"
            :maxlength="NOTE_MAX"
            required
            :disabled="flagging"
          ></textarea>
        </label>
        <label class="block text-sm mb-3" for="help-flag-expected">
          What I expected
          <textarea
            id="help-flag-expected"
            v-model="flagNotes.expected"
            class="w-full border rounded p-2 mt-1 min-h-20 text-sm"
            :maxlength="NOTE_MAX"
            required
            :disabled="flagging"
          ></textarea>
        </label>
        <label class="block text-sm mb-4" for="help-flag-better">
          What it should do better
          <textarea
            id="help-flag-better"
            v-model="flagNotes.better"
            class="w-full border rounded p-2 mt-1 min-h-20 text-sm"
            :maxlength="NOTE_MAX"
            required
            :disabled="flagging"
          ></textarea>
        </label>
        <div class="flex flex-row justify-end gap-2">
          <button type="button" class="cancel" :disabled="flagging" @click="closeFlag">Cancel</button>
          <button type="submit" class="primary" :disabled="flagging || !flagReady">Flag chat</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import { IconButton } from '@comp';
import { appConfig, callable, db } from '../../../db.js';
import HelpBotAvatar from './HelpBotAvatar.vue';
import HelpChatContent from './HelpChatContent.vue';
import HelpChatFiles from './HelpChatFiles.vue';
import HelpChatSettings from './HelpChatSettings.vue';
import { conversationData, conversationText, exportFilename } from './helpChatExport.mjs';
import { resolveSettings } from './helpChatSettings.mjs';
import { plainText } from './helpMessage.mjs';

defineProps({
  showExpand: Boolean,
});

const emit = defineEmits(['close', 'expand']);

const NOTE_MAX = 2000;

const store = useStore();
const toast = useToast();
const selectedId = ref(null);
const creating = ref(false);
const deletingId = ref(null);
const downloadOpen = ref(false);
const downloadDialog = ref(null);
const flagOpen = ref(false);
const flagging = ref(false);
const flagWrong = ref(null);
const flagNotes = reactive({
  wrong: '',
  expected: '',
  better: '',
});
const ready = ref(false);
const view = ref('chat');

const settings = computed(() => resolveSettings(store.state.helpChatSettings));
const viewLabel = computed(() => {
  if (view.value === 'settings') return 'Settings';
  if (view.value === 'files') return 'Files';
  return 'Help Chat';
});

const messageCount = computed(() => {
  const messages = store.state.helpChatList?.[selectedId.value]?.Messages;
  return Array.isArray(messages) ? messages.length : 0;
});

const flagReady = computed(() => ['wrong', 'expected', 'better'].every((key) => {
  const text = flagNotes[key].trim();
  return text.length > 0 && text.length <= NOTE_MAX;
}));

watch(selectedId, () => {
  flagOpen.value = false;
});

const userId = computed(() => {
  const id = store.getters.userID || store.state.user?.Basic?.key;
  return id && id !== 'Unknown' ? id : null;
});

const conversations = computed(() => {
  const list = store.state.helpChatList || {};
  return Object.entries(list)
    .filter(([, conversation]) => conversation && typeof conversation === 'object')
    .map(([id, conversation]) => ({
      id,
      title: conversation.Title || 'New conversation',
      updatedAt: conversation.UpdatedAt || conversation.CreatedAt || '',
    }))
    .sort((left, right) => String(right.updatedAt).localeCompare(String(left.updatedAt)));
});

function formatUpdated(timestamp) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function titleFromMessages(messages) {
  const first = (messages || []).find((message) => message?.Role === 'user' && message.Message);
  return first ? plainText(first.Message).slice(0, 80) : 'Previous conversation';
}

async function migrateLegacyConversation() {
  const root = await db.once(`HelpChats/${userId.value}`);
  if (!root?.Messages || !Array.isArray(root.Messages) || root.Conversations?.[userId.value]) return;

  const record = {
    ID: userId.value,
    User: userId.value,
    Title: titleFromMessages(root.Messages),
    Messages: root.Messages,
    CreatedAt: root.CreatedAt || new Date().toISOString(),
    UpdatedAt: root.Messages[root.Messages.length - 1]?.Time || root.CreatedAt || new Date().toISOString(),
  };
  if (root.HelpContext) record.HelpContext = root.HelpContext;
  await db.set(`HelpChats/${userId.value}/Conversations/${userId.value}`, record);
}

async function rememberSelection(id) {
  selectedId.value = id;
  if (!userId.value || !id) return;
  await db.update(`HelpChats/${userId.value}`, { ActiveConversation: id });
}

async function startConversation() {
  if (!userId.value || creating.value) return;
  creating.value = true;
  try {
    const now = new Date().toISOString();
    const ref = await db.push(`HelpChats/${userId.value}/Conversations`, {
      User: userId.value,
      Title: 'New conversation',
      Messages: [],
      CreatedAt: now,
      UpdatedAt: now,
    });
    await rememberSelection(ref.key);
    view.value = 'chat';
  } finally {
    creating.value = false;
  }
}

async function loadConversations() {
  if (!userId.value || ready.value) return;
  await db.update(`HelpChats/${userId.value}`, { User: userId.value });
  await migrateLegacyConversation();
  db.watch(`HelpChats/${userId.value}/Settings`, store, 'helpChatSettings')
    .catch((error) => console.error('[HelpChat] Failed to load settings', error));
  db.watch(`HelpChats/${userId.value}/GeneratedFiles`, store, 'helpChatFiles')
    .catch((error) => console.error('[HelpChat] Failed to load files', error));
  await db.watch(`HelpChats/${userId.value}/Conversations`, store, 'helpChatList');
  const root = await db.once(`HelpChats/${userId.value}`);
  const list = store.state.helpChatList || {};
  const focusId = store.state.helpChatFocus?.conversationId;
  const active = root?.ActiveConversation;
  if (focusId && list[focusId]) {
    selectedId.value = focusId;
    view.value = 'chat';
  } else if (active && list[active]) {
    selectedId.value = active;
  } else if (conversations.value.length) {
    selectedId.value = conversations.value[0].id;
  } else {
    await startConversation();
  }
  ready.value = true;
}

function selectConversation(id) {
  rememberSelection(id);
}

function openConversation(id) {
  rememberSelection(id);
  view.value = 'chat';
}

function openDownload() {
  flagOpen.value = false;
  downloadOpen.value = true;
}

function saveDownload(format) {
  const conversation = store.state.helpChatList?.[selectedId.value];
  if (!conversation) return;
  const exportedAt = new Date().toISOString();
  const title = conversation.Title || 'New conversation';
  const isData = format === 'data';
  const body = isData
    ? `${JSON.stringify(conversationData(conversation, { id: selectedId.value, exportedAt }), null, 2)}\n`
    : conversationText(conversation, { botName: settings.value.BotName, exportedAt });
  const blob = new Blob([body], { type: `${isData ? 'application/json' : 'text/plain'};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = exportFilename(title, isData ? 'json' : 'txt');
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  downloadOpen.value = false;
}

watch(downloadOpen, (open) => {
  if (open) nextTick(() => downloadDialog.value?.focus());
});

function openFlag() {
  downloadOpen.value = false;
  flagNotes.wrong = '';
  flagNotes.expected = '';
  flagNotes.better = '';
  flagOpen.value = true;
}

function closeFlag() {
  if (flagging.value) return;
  flagOpen.value = false;
}

function callableMessage(error, fallback) {
  const message = typeof error?.message === 'string' ? error.message.trim() : '';
  if (!message || /^(internal|unavailable|deadline-exceeded|unknown)$/i.test(message)) return fallback;
  return message;
}

async function submitFlag() {
  const conversationId = selectedId.value;
  if (!conversationId || !flagReady.value || flagging.value) return;
  flagging.value = true;
  try {
    const databaseURL = appConfig?.firebaseConfig?.databaseURL;
    if (!databaseURL) throw new Error('Database configuration is missing.');
    await callable('helpChatFlag')({
      databaseURL,
      conversationId,
      wrong: flagNotes.wrong.trim(),
      expected: flagNotes.expected.trim(),
      better: flagNotes.better.trim(),
    });
    flagOpen.value = false;
    toast.success('Chat flagged.');
  } catch (error) {
    console.error('[HelpChat] Failed to flag chat', error);
    toast.error(callableMessage(error, 'Could not flag this chat.'));
  } finally {
    flagging.value = false;
  }
}

watch(flagOpen, (open) => {
  if (open) nextTick(() => flagWrong.value?.focus());
});

async function deleteConversation(id) {
  if (!userId.value || !id || deletingId.value) return;
  deletingId.value = id;
  try {
    const databaseURL = appConfig?.firebaseConfig?.databaseURL;
    if (databaseURL) {
      await callable('helpChatFiles')({ action: 'purge', conversationId: id, databaseURL });
    }
    const remaining = conversations.value.filter((conversation) => conversation.id !== id);
    await db.delete(`HelpChats/${userId.value}/Conversations/${id}`);
    if (selectedId.value !== id) return;
    if (remaining.length) {
      await rememberSelection(remaining[0].id);
      return;
    }
    selectedId.value = null;
    await db.update(`HelpChats/${userId.value}`, { ActiveConversation: null });
  } finally {
    deletingId.value = null;
  }
}

watch(() => {
  const id = store.state.helpChatFocus?.conversationId;
  return id && store.state.helpChatList?.[id] ? id : null;
}, (id) => {
  if (!id) return;
  selectedId.value = id;
  view.value = 'chat';
});

onMounted(() => {
  loadConversations();
});
</script>
